import * as Sentry from "@sentry/react-native";
import { CommonActions } from "@react-navigation/native";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";
import useCountDown from "react-countdown-hook";

import Text from "@components/CustomText";
import color from "@constants/color";
import t from "@constants/localization";
import testIds from "@constants/testIds";
import { IDFA } from "@lib/idfa";
import { actions as authActions } from "@reducers/authReducer";
import analytics from "@services/analytics";
import getOrCreateWebDeviceId from "@utils/getOrCreateWebDeviceId";
import { getRefererData } from "@utils/PlayReferrer";
import { generateLinkParams } from "@utils/URL";
import toast from "@utils/toast";
import {
  DeviceEventEmitter,
  InteractionManager,
  Keyboard,
  Platform,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import RNAndroidSmsRetriever from "react-native-android-sms-retriever";
import DeviceInfo, { getUniqueId } from "react-native-device-info";
import { AppEventsLogger } from "react-native-fbsdk-next";
import { useDispatch, useSelector } from "react-redux";
import { androidAppStore } from "@config";
import CityMallLogoBlack from "../../assets/svg/CityMallLogoBlack";
import PrimaryButton from "../../components/PrimaryButton";
import {
  SIGNUP_FLOW_EVENTS,
  SIGNUP_FLOW_SOURCES,
} from "../../constants/analyticsEventsCT";
import { sendOtpToUser, verifyOtpForLogin } from "../../services/api";

const COMPLETE_SIGNIN = "fb_mobile_activate_app";

const PATH = "screens/Auth/EnterOTP";

const EnterOTP = (props) => {
  const {
    navigation,
    route: { params: { phoneNumber = "", checked = false } = {} } = {},
  } = props;

  const dispatch = useDispatch();
  const success = useSelector((s) => s.auth.authSuccess);

  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referrer, setReferrer] = useState({});
  const [countdown, { start }] = useCountDown(30 * 1000, 1000);
  const [isCountdownRunning, setIsCountdownRunning] = useState(true);
  const [shouldShowResendOption, setShouldShowResendOption] = useState(true);
  const applinkData =
    useSelector((s) => s.deepLink?.referer?.hashResult?.data) || {};

  const firstDigitRef = useRef(null);
  const secondDigitRef = useRef(null);
  const thirdDigitRef = useRef(null);
  const fourthDigitRef = useRef(null);

  const isAutoReadOtp = useRef(false);

  // useEffect(() => {
  //   if (Platform.OS === "android") {
  //    // RNAndroidSmsRetriever.startSmsRetriever();
  //     DeviceEventEmitter.addListener("smsRetrievedSuccess", (event) => {
  //       if (event.message) {
  //         const parsedOtp = event.message.match(/\d{4}/)[0];
  //         if (parsedOtp && parsedOtp.length === 4) {
  //           isAutoReadOtp.current = true;
  //           setOtp(parsedOtp);
  //           DeviceEventEmitter.removeListener("smsRetrievedSuccess", null);
  //         }
  //       }
  //     });
  //   }

  //   getRefererData().then(setReferrer).catch(e => Sentry.captureException(new Error(e.message)));

  //   return () => {
  //     DeviceEventEmitter.removeListener("smsRetrievedSuccess", null);
  //   };
  // }, []);

  useEffect(() => {
    start();
  }, [start]);

  const resetCountdown = useCallback(() => {
    start();
  }, [start]);

  useEffect(() => {
    if (countdown === 0 && shouldShowResendOption) {
      setIsCountdownRunning(true);
    } else if (countdown === 0) {
      setShouldShowResendOption(true);
      setIsCountdownRunning(false);
    } else {
      setIsCountdownRunning(true);
      setShouldShowResendOption(false);
    }
  }, [countdown, shouldShowResendOption]);

  useEffect(() => {
    if (otp && otp.length === 4) {
      Keyboard.dismiss();
    }
  }, [otp]);

  const goBack = () => {
    analytics.trackEvent(SIGNUP_FLOW_EVENTS.CHANGE_NUMBER);
    navigation && navigation.goBack();
  };

  const getOtpPayload = async () => {
    const [device_fingerprint, carrier, device, ip] = await Promise.all([
      DeviceInfo.getFingerprint(),
      DeviceInfo.getCarrier(),
      DeviceInfo.getDevice(),
      DeviceInfo.getIpAddress(),
    ]);

    const deviceInfo = {
      device_fingerprint,
      device_type: DeviceInfo.getDeviceType(),
      carrier,
      device,
      os: Platform.OS,
      ip,
      ver: `${DeviceInfo.getBuildNumber()}`,
      os_version: `${DeviceInfo.getSystemVersion()}`,
      brand: DeviceInfo.getBrand(),
      model: DeviceInfo.getDeviceId(),
    };
    const idfa = await new Promise((resolve, reject) => {
      IDFA.getIDFA()
        .then((deviceIdfa) => {
          resolve(deviceIdfa);
        })
        .catch((e) => {
          resolve(null);
        });
    });

    let { installReferrer } = referrer || {};
    try {
      if (installReferrer) {
        const refererParams = generateLinkParams(
          decodeURIComponent(installReferrer),
        ).getAll();
        if (refererParams && !!Object.values(refererParams).length) {
          installReferrer = refererParams;
        }
      }
    } catch (e) {
      console.log(e);
    }

    const tracking_info = {
      referrer,
      ...applinkData,
      app_store: Platform.OS === "ios" ? "appstore" : androidAppStore,
    };

    // On web the native device modules are shimmed to noop → getUniqueId()/idfa
    // come back undefined → the session row would store NULL. Fall back to a
    // persistent per-browser UUID so web sessions are backtraceable. Native is
    // unaffected (real values make `|| webId` never trigger).
    const webId = await getOrCreateWebDeviceId();

    const payload = {
      phone_number: phoneNumber,
      device_info: JSON.stringify(deviceInfo),
      uniqueDeviceId: (await getUniqueId()) || webId,
      idfa: idfa || webId,
      tracking_info: JSON.stringify(tracking_info),
      user_type: "NON_TC_USER",
      enable_device_verification: false,
    };

    return payload;
  };

  const isOtpDeleting = useRef(false);

  const onChangeOtp = (value) => {
    if (value.trim()) {
      if (typeof otp === "string" && otp.length < 4) {
        const newOtp = `${otp}${
          value.trim().length === 1
            ? value.trim()
            : value.trim().charAt(value.trim().length - 1)
        }`.replace(/\D/g, "");
        setOtp(newOtp);
        isOtpDeleting.current = false;
      } else {
        if (otp === null) {
          setOtp(value);
        }
      }
    }
  };

  useEffect(() => {
    if (otp) {
      switch (otp.length) {
        case 1:
          isOtpDeleting.current
            ? firstDigitRef.current.focus()
            : secondDigitRef.current.focus();
          break;
        case 2:
          isOtpDeleting.current
            ? secondDigitRef.current.focus()
            : thirdDigitRef.current.focus();
          break;
        case 3:
          isOtpDeleting.current
            ? thirdDigitRef.current.focus()
            : fourthDigitRef.current.focus();
          break;
        default:
          break;
      }
    }
  }, [otp]);

  const onBackPress = ({ nativeEvent: { key: value } }) => {
    if (value === "Backspace") {
      isOtpDeleting.current = true;
      setOtp((o) => o?.slice(0, -1) ?? "");
    }
  };

  const showToast = (errorMessage) => {
    toast.showLongToast(errorMessage);
  };

  const resetState = () => {
    setOtp("");
    setIsSubmitting(false);
    setTimeout(() => {
      firstDigitRef.current?.focus();
    }, 3000);
  };

  const userToken = useSelector((s) => s.auth.userToken);

  useEffect(() => {
    if (userToken) {
      resetState();
    }
  }, [userToken]);

  const onSubmit = debounce(
    async () => {
      const automaticOtp = otp;
      if (!automaticOtp) return;
      if (isSubmitting) return;
      setIsSubmitting(true);
      const otpPayload = await getOtpPayload();
      const payload = {
        ...otpPayload,
        otp: automaticOtp,
        auto_read_otp: isAutoReadOtp.current,
      };
      try {
        const { data } = await verifyOtpForLogin(payload, checked);
        const {
          token = "",
          isDeviceVerified = false,
          ivrSessionId = "",
          newTcUser = false,
          callingLimitExceed = null,
        } = data;
        if (isDeviceVerified) {
          dispatch(
            authActions.signIn({
              verifyOtpResponse: data,
              userToken: token,
            }),
          );
          analytics.trackEvent(SIGNUP_FLOW_EVENTS.NUMBER_VERIFIED, {
            number_source: SIGNUP_FLOW_SOURCES.STANDARD,
          });
          analytics.trackEvent(SIGNUP_FLOW_EVENTS.SUCCESSFULL_LOGIN);
          AppEventsLogger.logEvent(COMPLETE_SIGNIN, {
            currency: "INR",
          });
        } else {
          const resetAction = CommonActions.reset({
            index: 0,
            routes: [
              {
                name: "TwoFactorAuth",
                params: {
                  ivrSessionId,
                  newTcUser,
                  phone_number: data?.teamLeader?.phone_number,
                  callingLimitExceed,
                },
              },
            ],
          });
          navigation.dispatch(resetAction);
          resetState();
        }
      } catch (err) {
        if (err?.response?.data?.message) {
          // showToast(err.response.data.message);
        } else {
          showToast(t(`${PATH}/tryAgain`));
        }
        resetState();
      }
    },
    200,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    InteractionManager.runAfterInteractions(() => {
      firstDigitRef.current?.focus();
    });
  }, []);

  const resendOtp = () => {
    if (firstDigitRef) {
      setOtp("");
      firstDigitRef.current?.focus();
    }
    resetCountdown();
    sendOtpToUser(phoneNumber).catch((err) => console.warn(err));
    setIsSubmitting(false);
    analytics.trackEvent(SIGNUP_FLOW_EVENTS.RESEND_OTP);
  };

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <CityMallLogoBlack height={30} width={114} />
        </View>
        <View style={styles.title}>
          <Text variant="body_text_1" style={styles.subTitleText}>
            {t(`${PATH}/enterOTP`)}
          </Text>
        </View>
        <View style={styles.title}>
          <Text variant="heading_24" style={styles.titleText}>
            {t(`${PATH}/verifyNumber`)}
          </Text>
        </View>
        <View testID={testIds.Otp_Box} style={styles.enterPhone}>
          {[firstDigitRef, secondDigitRef, thirdDigitRef, fourthDigitRef].map(
            (ref, index) => (
              <TextInput
                testID={`${testIds.Otp_Input_}${index}`}
                ref={ref}
                value={otp?.[index] ?? ""}
                clearTextOnFocus
                // editable={!isSubmitting}
                style={styles.otpInput}
                maxLength={10}
                keyboardType="numeric"
                onChangeText={onChangeOtp}
                onKeyPress={onBackPress}
                key={`key${index}`}
              />
            ),
          )}
        </View>
        <View
          style={{
            flexDirection: "row",
            marginHorizontal: 20,
            marginTop: 16,
          }}
        >
          <Text variant="body_text_1">
            00:
            {isCountdownRunning
              ? `${countdown / 1000}`.length === 1
                ? `0${countdown / 1000}`
                : countdown / 1000
              : "00"}
          </Text>
          <TouchableOpacity
            testID="enter_otp_resent_otp"
            disabled={isCountdownRunning}
            onPress={resendOtp}
            style={{
              paddingHorizontal: 10,
              opacity: isCountdownRunning ? 0.3 : 1,
            }}
            hitSlop={{ top: 20, bottom: 20, left: 10, right: 10 }}
          >
            <Text
              variant="body_text_1"
              style={{
                color: isCountdownRunning ? color.lightBlack : color.primary,
              }}
            >
              {t(`${PATH}/resend`)}
            </Text>
          </TouchableOpacity>
        </View>
        <View
          style={{
            flexDirection: "row",
            marginTop: 24,
            alignItems: "center",
            marginHorizontal: 20,
          }}
        >
          <PrimaryButton
            testID={testIds.Enter_Otp}
            disabled={!(otp && otp.length === 4) || success}
            onPress={onSubmit}
            text={t(`${PATH}/goAhead`)}
            loading={isSubmitting}
            buttonStyle={{ marginRight: 20 }}
          />
        </View>
        <View
          style={{
            flexDirection: "row",
            paddingHorizontal: 20,
            marginTop: 36,
            flexWrap: "wrap",
          }}
        >
          <Text
            variant="caption_2"
            style={{
              color: color.lightBlack,
              paddingRight: 10,
            }}
          >
            {t(`${PATH}/OTPSent`, { phone: phoneNumber })}
          </Text>
          <TouchableOpacity
            testID="enter_otp_change_number"
            onPress={goBack}
            hitSlop={{ top: 20, bottom: 20, left: 10, right: 10 }}
          >
            <Text
              variant="caption_2"
              style={{
                color: color.primary,
              }}
            >
              {t(`${PATH}/changeNumber`)}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );
};

export default EnterOTP;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  header: {
    padding: 20,
    marginBottom: 40,
    paddingLeft: 15,
  },
  title: {
    paddingHorizontal: 20,
    color: color.lightBlack,
    marginBottom: 10,
  },
  subTitleText: {
    color: color.lightBlack,
    fontSize: 16,
  },
  titleText: {
    fontWeight: "bold",
    fontSize: 24,
  },
  enterPhone: {
    flexDirection: "row",
    paddingHorizontal: 20,
  },
  otpInput: {
    borderColor: color.lighterBlack,
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    minWidth: 50,
    fontSize: 16,
    minHeight: 40,
    textAlign: "center",
    marginRight: 10,
    width: 40,
  },
});
